import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Pages.css';

const emptyForm = { name: '', email: '', password: '', phone: '', role: 'client' };

function AdminUsers({ apiUrl, token }) {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = () => {
    axios
      .get(`${apiUrl}/admin/users`, { headers })
      .then((res) => setUsers(res.data))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load users'));
  };

  useEffect(fetchUsers, [apiUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingId(user.id);
    setFormData({ name: user.name || '', email: user.email, password: '', phone: user.phone || '', role: user.role });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await axios.patch(`${apiUrl}/admin/users/${editingId}`, payload, { headers });
      } else {
        await axios.post(`${apiUrl}/admin/users`, formData, { headers });
      }
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const toggleActive = async (user) => {
    try {
      if (user.is_active) {
        await axios.delete(`${apiUrl}/admin/users/${user.id}`, { headers });
      } else {
        await axios.patch(`${apiUrl}/admin/users/${user.id}`, { is_active: true }, { headers });
      }
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const filtered = filter === 'all' ? users : users.filter((u) => u.role === filter);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Manage Users</h1>
        <button className="btn btn-primary" onClick={openCreate}>+ New User</button>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="tabs">
        {['all', 'client', 'practitioner', 'admin'].map((r) => (
          <button key={r} className={`tab-btn ${filter === r ? 'active' : ''}`} onClick={() => setFilter(r)}>
            {r === 'all' ? 'All' : `${r}s`}
          </button>
        ))}
      </div>

      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id}>
                <td>{u.name || <span className="text-muted">—</span>}</td>
                <td>{u.email}</td>
                <td><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                <td><span className={`badge ${u.is_active ? 'badge-active' : 'badge-inactive'}`}>{u.is_active ? 'active' : 'deactivated'}</span></td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)} style={{ marginRight: 6 }}>Edit</button>
                  <button className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => toggleActive(u)}>
                    {u.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="empty-state">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit User' : 'New User'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name</label>
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>{editingId ? 'New Password (leave blank to keep current)' : 'Password'}</label>
                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingId} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  <option value="client">Client</option>
                  <option value="practitioner">Practitioner</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary btn-block">{editingId ? 'Save Changes' : 'Create User'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
